import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import { routeTree } from './routeTree.gen'
import { auth, initAuth } from './lib/auth'
import { capturePageview, initAnalytics } from './lib/posthog'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session is gone; retrying just delays the redirect.
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403) return false
        return failureCount < 2
      },
    },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient, auth },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

async function main() {
  // Before the first render, so a flag read during it gets a real answer
  // rather than its fallback. A no-op with no key configured.
  initAnalytics()

  // Resolve the stored session BEFORE the first render. Without this await, the
  // `_authed` guard would run against `auth.session === null` and bounce an
  // already-logged-in user to /login on every page load.
  await initAuth(() => {
    void router.invalidate()
    void queryClient.invalidateQueries()
  })

  // By hand, because TanStack navigates on the client: PostHog's own listener
  // fires once per page LOAD and would report one pageview per session.
  capturePageview(window.location.pathname)
  router.subscribe('onResolved', ({ toLocation }) => capturePageview(toLocation.pathname))

  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('index.html is missing <div id="root">')

  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}

void main()
